import React from 'react';
import { Redirect, Switch } from 'react-router-dom';
import { Application, Module, Privilege } from '@fides/fides-react-components';
import { useGlobalConfigContext } from 'core/contexts/GlobalConfig';
import { PrivateRoute } from '../helpers';
import { hasAccessToModuleOrImpersonated } from './accessUtil';
import { addAndEditPageAccessGranted } from '../helpers/pageAccessHelper';

const Bank = React.lazy(() => import('../../components/pages/Banks'));
const BankViewPage = React.lazy(() => import('../../components/pages/Banks/BankDetails/BankViewPage'));
const BankEditor = React.lazy(() => import('../../components/pages/Banks/BankDetails/BankEditor'));
const Clients = React.lazy(() => import('../../components/pages/Client'));
const PendingPaymentApprovals = React.lazy(() => import('../../components/pages/PendingApprovals/Payments'));
const Owner = React.lazy(() => import('../../components/pages/AccountOwners'));
const OwnerViewPage = React.lazy(() => import('../../components/pages/AccountOwners/OwnerDetails/OwnerViewPage'));
const OwnerEditor = React.lazy(() => import('../../components/pages/AccountOwners/OwnerDetails/OwnerEditor'));
const Template = React.lazy(() => import('../../components/pages/Templates'));
const TemplateViewPage = React.lazy(() => import('../../components/pages/Templates/pages/TemplateViewPage'));
const TemplateEditor = React.lazy(() => import('../../components/pages/Templates/pages/TemplateEditor'));
const TemplateRegulatoryReportingView = React.lazy(
  () => import('../../components/pages/Templates/pages/TemplateRegulatoryReportingView')
);
const TemplateStructuredRemittanceInformationView = React.lazy(
  () => import('../../components/pages/Templates/pages/TemplateStructuredRemittanceInformationView')
);
const RegulatoryReportingEditor = React.lazy(
  () =>
    import(
      '../../components/pages/Payment/pages/RegulatoryReporting/RegulatoryReportingDetails/RegulatoryReportingEditor'
    )
);
const StructuredRemittanceInformationEditor = React.lazy(
  () =>
    import(
      '../../components/pages/Payment/pages/StructuredRemittanceInformation/StructuredRemittanceInformationDetails/StructuredRemittanceInformationEditor'
    )
);
const PendingLiquidityMgmtApprovals = React.lazy(() => import('../../components/pages/PendingApprovals/LiquidityMgmt'));
const PendingPaymentApprovalDetails = React.lazy(
  () => import('../../components/pages/PendingApprovalDetails/Payments')
);
const PendingLiquidityMgmtApprovalDetails = React.lazy(
  () => import('../../components/pages/PendingApprovalDetails/LiquidityMgmt')
);

export const AdministrationRouter = () => {
  const { accessHelper, twoMRHelper, hasUserAppAccess, user } = useGlobalConfigContext();
  const hasAccessToClients =
    !accessHelper.isImpersonated &&
    (accessHelper.isFidesAdministrator(Application.EFTWEB) ||
      accessHelper.isFidesReadOnlyAdministrator(Application.EFTWEB));
  const hasAccessToPaymentApprovals =
    twoMRHelper.is2MREnabled &&
    (twoMRHelper.hasUserAccessToActive2MRModule ||
      (accessHelper.isImpersonatedAsClient && twoMRHelper.is2MREnabledForAnyModule));
  const hasAccessToLiquidityMgmtApprovals =
    twoMRHelper.is2MREnabled &&
    (twoMRHelper.is2MREnabledFor(Module.CASHPOOL) ||
      (accessHelper.isImpersonatedAsClient && twoMRHelper.is2MREnabledFor(Module.CASHPOOL)));

  const hasAccessToBanksScreen: boolean =
    hasUserAppAccess(Application.EFTWEB) && hasAccessToModuleOrImpersonated(accessHelper, Module.BANK);

  const hasBankEditorAccess: boolean = addAndEditPageAccessGranted(accessHelper, user, Module.BANK, Application.EFTWEB);
  const hasAccessToOwnersScreen: boolean =
    hasUserAppAccess(Application.EFTWEB) && hasAccessToModuleOrImpersonated(accessHelper, Module.OWNER);

  const hasOwnerEditorAccess: boolean = addAndEditPageAccessGranted(
    accessHelper,
    user,
    Module.OWNER,
    Application.EFTWEB
  );

  const hasAccessToTemplateScreen: boolean =
    hasUserAppAccess(Application.EFTWEB) &&
    (hasAccessToModuleOrImpersonated(accessHelper, Module.TEMPLATE) ||
      hasAccessToModuleOrImpersonated(accessHelper, Module.PRIVATE_TEMPLATE));

  const hasTemplateEditorAccess: boolean =
    hasUserAppAccess(Application.EFTWEB) &&
    (addAndEditPageAccessGranted(accessHelper, user, Module.TEMPLATE, Application.EFTWEB) ||
      (accessHelper.isImpersonatedAsClient && accessHelper.isFidesAdministrator(Application.EFTWEB)) ||
      (!user.impersonated && accessHelper.hasPermissionForModule(Module.PRIVATE_TEMPLATE, Privilege.ADMIN)) ||
      (!!user.impersonatedUser && accessHelper.hasPermissionForModule(Module.PRIVATE_TEMPLATE, Privilege.ADMIN)));

  const basePendingApprovalsPath = '/administration/pending-approvals';

  const getPendingApprovalsPath = (): string => {
    if (hasAccessToPaymentApprovals) {
      return `${basePendingApprovalsPath}/payments`;
    }
    if (hasAccessToLiquidityMgmtApprovals) {
      return `${basePendingApprovalsPath}/liquidity-management`;
    }
    return '/page-not-found';
  };

  const adminRoutes = [
    {
      path: '/administration/bank/editor/:uniqueId',
      hasAccess: hasBankEditorAccess,
      component: <BankEditor />,
    },
    {
      path: '/administration/bank/editor/',
      hasAccess: hasBankEditorAccess,
      component: <BankEditor />,
    },
    {
      path: '/administration/bank/:uniqueId',
      hasAccess: hasAccessToBanksScreen,
      component: <BankViewPage />,
    },
    {
      path: '/administration/bank',
      hasAccess: hasAccessToBanksScreen,
      component: <Bank />,
    },
    {
      path: '/administration/client',
      hasAccess: hasAccessToClients,
      component: <Clients />,
    },
    {
      path: '/administration/owner/editor/:uniqueId',
      hasAccess: hasOwnerEditorAccess,
      component: <OwnerEditor />,
    },
    {
      path: '/administration/owner/editor/',
      hasAccess: hasOwnerEditorAccess,
      component: <OwnerEditor />,
    },
    {
      path: '/administration/owner/:uniqueId',
      hasAccess: hasAccessToOwnersScreen,
      component: <OwnerViewPage />,
    },
    {
      path: '/administration/owner',
      hasAccess: hasAccessToOwnersScreen,
      component: <Owner />,
    },
    {
      path: '/administration/template/regulatoryReportingDetails/editor/:uniqueId?',
      hasAccess: hasTemplateEditorAccess,
      component: <RegulatoryReportingEditor />,
    },
    {
      path: '/administration/template/regulatoryReportingDetails/:uniqueId',
      hasAccess: hasAccessToTemplateScreen,
      component: <TemplateRegulatoryReportingView />,
    },
    {
      path: '/administration/template/structuredRemittanceInformation/:action(add|edit)/:uniqueId?',
      hasAccess: hasTemplateEditorAccess,
      component: <StructuredRemittanceInformationEditor />,
    },
    {
      path: '/administration/template/structuredRemittanceInformation/:uniqueId',
      hasAccess: hasAccessToTemplateScreen,
      component: <TemplateStructuredRemittanceInformationView />,
    },
    {
      path: '/administration/template/:uniqueId/edit',
      hasAccess: hasTemplateEditorAccess,
      component: <TemplateEditor />,
    },
    {
      path: '/administration/template/add',
      hasAccess: hasTemplateEditorAccess,
      component: <TemplateEditor />,
    },
    {
      path: '/administration/template/:uniqueId/view',
      hasAccess: hasAccessToTemplateScreen,
      component: <TemplateViewPage />,
    },
    {
      path: '/administration/template',
      hasAccess: hasAccessToTemplateScreen,
      component: <Template />,
    },
    {
      path: `${basePendingApprovalsPath}/payments`,
      hasAccess: hasAccessToPaymentApprovals,
      component: <PendingPaymentApprovals />,
    },
    {
      path: `${basePendingApprovalsPath}/payments/:uniqueId`,
      hasAccess: hasAccessToPaymentApprovals,
      component: <PendingPaymentApprovalDetails />,
    },
    {
      path: `${basePendingApprovalsPath}/liquidity-management`,
      hasAccess: hasAccessToLiquidityMgmtApprovals,
      component: <PendingLiquidityMgmtApprovals />,
    },
    {
      path: `${basePendingApprovalsPath}/liquidity-management/:uniqueId`,
      hasAccess: hasAccessToLiquidityMgmtApprovals,
      component: <PendingLiquidityMgmtApprovalDetails />,
    },
  ];

  return (
    <Switch>
      <Redirect from='/administration/' to='/administration/client' exact />
      <Redirect from={basePendingApprovalsPath} to={getPendingApprovalsPath()} exact />
      {adminRoutes.map(({ path, hasAccess, component }) => (
        <PrivateRoute key={path} path={path} exact>
          {hasAccess ? component : <Redirect to='/page-not-found' />}
        </PrivateRoute>
      ))}
      <Redirect to='/page-not-found' />
    </Switch>
  );
};
